import { Injectable, signal } from '@angular/core';

/** localStorage key for per-event viewed alarm ids (client-only; no API). */
export const VIEWED_ALARMS_STORAGE_KEY = 'eventq:viewed-alarms';

type ViewedAlarmsByEvent = Record<string, string[]>;

const EMPTY_VIEWED_IDS: ReadonlySet<string> = new Set();

function readStoredViewedAlarms(): ViewedAlarmsByEvent {
  try {
    const raw = localStorage.getItem(VIEWED_ALARMS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const result: ViewedAlarmsByEvent = {};
    for (const [eventId, ids] of Object.entries(parsed)) {
      if (!Array.isArray(ids)) {
        continue;
      }
      result[eventId] = ids.filter((id): id is string => typeof id === 'string');
    }
    return result;
  } catch {
    return {};
  }
}

function writeStoredViewedAlarms(state: ViewedAlarmsByEvent): void {
  try {
    if (Object.keys(state).length === 0) {
      localStorage.removeItem(VIEWED_ALARMS_STORAGE_KEY);
      return;
    }
    localStorage.setItem(VIEWED_ALARMS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Session continues from in-memory state when storage is blocked or full.
  }
}

/** Persists which alarm clips have been viewed, keyed by event id. */
@Injectable({ providedIn: 'root' })
export class AlarmViewedStorageService {
  private readonly viewedByEvent = signal<ViewedAlarmsByEvent>(readStoredViewedAlarms());

  isViewed(eventId: string, alarmId: string): boolean {
    return this.viewedByEvent()[eventId]?.includes(alarmId) ?? false;
  }

  /** One lookup set for a whole alarm list. Reads the viewed signal a single time. */
  viewedIdSet(eventId: string): ReadonlySet<string> {
    const ids = this.viewedByEvent()[eventId];
    return ids?.length ? new Set(ids) : EMPTY_VIEWED_IDS;
  }

  markViewed(eventId: string, alarmId: string): void {
    const current = this.viewedByEvent()[eventId] ?? [];
    if (current.includes(alarmId)) {
      return;
    }
    const next = { ...this.viewedByEvent(), [eventId]: [...current, alarmId] };
    this.viewedByEvent.set(next);
    writeStoredViewedAlarms(next);
  }

  clearEvent(eventId: string): void {
    const current = { ...readStoredViewedAlarms(), ...this.viewedByEvent() };
    if (!(eventId in current)) {
      return;
    }
    const { [eventId]: _removed, ...rest } = current;
    this.viewedByEvent.set(rest);
    writeStoredViewedAlarms(rest);
  }
}
